import {
    Typography,
    Box,
    Paper,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useState, useEffect } from 'react';
import { Event, EventFilterType, EventDestinationType } from '../services/monitor-api.service';
import { EventDetailsDialog, getStatusColor, getStatusSx } from './EventDetailsDialog';
import { monitorApi } from '../services/monitor-api.service';
import { useAuth } from '../contexts/AuthContext';
import { Action, canPerformAction } from '../services/permissions.service';

const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('sv-SE'); // Generates ISO-like format
};

interface EventsListProps {
    refreshTrigger?: number;
}

export function EventsList({ refreshTrigger }: EventsListProps) {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [retryingEventId, setRetryingEventId] = useState<number | null>(null);
    const [markingDeadEventId, setMarkingDeadEventId] = useState<number | null>(null);
    const [markingHandledEventId, setMarkingHandledEventId] = useState<number | null>(null);
    const [markingWarningEventId, setMarkingWarningEventId] = useState<number | null>(null);
    const [filter, setFilter] = useState<EventFilterType>('all');
    const [destination, setDestination] = useState<EventDestinationType>('all');
    const [search, setSearch] = useState<string>('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { role, email } = useAuth();
    const canRetry = canPerformAction(role, Action.retryEvents);

    const fetchEvents = async (
        cursor?: string,
        size: number = 25,
        reset: boolean = false,
        currentFilter?: EventFilterType,
        currentDestination?: EventDestinationType,
        currentSearch?: string,
    ) => {
        try {
            setLoading(true);
            setError(null);

            const response = await monitorApi.getEvents(
                size,
                cursor,
                currentFilter || filter,
                currentDestination || destination,
                currentSearch !== undefined ? currentSearch : search,
            );

            if (reset || !cursor) {
                setEvents(response.events);
                setPage(0);
            } else {
                setEvents((prev) => [...prev, ...response.events]);
            }

            setHasMore(response.pagination.hasMore);
            setNextCursor(response.pagination.nextCursor);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            fetchEvents(undefined, pageSize, true);
        }
    }, [refreshTrigger, pageSize]);

    const handlePageChange = (_: unknown, newPage: number) => {
        setPage(newPage);

        // If we need more data and don't have it yet
        const neededEvents = (newPage + 1) * pageSize;
        if (neededEvents > events.length && hasMore && !loading) {
            fetchEvents(nextCursor || undefined, pageSize);
        }
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setPage(0);
        fetchEvents(undefined, newPageSize, true);
    };

    const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: EventFilterType | null) => {
        if (newFilter !== null) {
            setFilter(newFilter);
            setPage(0);
            fetchEvents(undefined, pageSize, true, newFilter, destination);
        }
    };

    const handleDestinationChange = (_: React.MouseEvent<HTMLElement>, newDestination: EventDestinationType | null) => {
        if (newDestination !== null) {
            setDestination(newDestination);
            setPage(0);
            fetchEvents(undefined, pageSize, true, filter, newDestination, search);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setPage(0);
            fetchEvents(undefined, pageSize, true, filter, destination, search);
        }
    };

    const handleSearchClear = () => {
        setSearch('');
        setPage(0);
        fetchEvents(undefined, pageSize, true, filter, destination, '');
    };

    const handleRetryEvent = async (eventId: number) => {
        try {
            setRetryingEventId(eventId);
            await monitorApi.forceRetry(eventId);
            // Fetch the updated event and update it in place
            const response = await monitorApi.getEvent(eventId);
            setEvents((prevEvents) => prevEvents.map((event) => (event.id === eventId ? response.event : event)));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to retry event');
        } finally {
            setRetryingEventId(null);
        }
    };

    const handleMarkDeadEvent = async (eventId: number) => {
        try {
            setMarkingDeadEventId(eventId);
            await monitorApi.markDead(eventId, email || undefined);
            // Fetch the updated event and update it in place
            const response = await monitorApi.getEvent(eventId);
            setEvents((prevEvents) => prevEvents.map((event) => (event.id === eventId ? response.event : event)));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to mark event as dead');
        } finally {
            setMarkingDeadEventId(null);
        }
    };

    const handleMarkHandledEvent = async (eventId: number) => {
        try {
            setMarkingHandledEventId(eventId);
            await monitorApi.markHandled(eventId, email || undefined);
            // Fetch the updated event and update it in place
            const response = await monitorApi.getEvent(eventId);
            setEvents((prevEvents) => prevEvents.map((event) => (event.id === eventId ? response.event : event)));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to mark event as handled');
        } finally {
            setMarkingHandledEventId(null);
        }
    };

    const handleMarkWarningEvent = async (eventId: number) => {
        try {
            setMarkingWarningEventId(eventId);
            await monitorApi.markWarning(eventId, email || undefined);
            // Fetch the updated event and update it in place
            const response = await monitorApi.getEvent(eventId);
            setEvents((prevEvents) => prevEvents.map((event) => (event.id === eventId ? response.event : event)));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to mark event as warning');
        } finally {
            setMarkingWarningEventId(null);
        }
    };

    const handleViewDetails = (event: Event) => {
        setSelectedEventId(event.id);
    };

    const handleCloseDetails = () => {
        setSelectedEventId(null);
    };

    const handleRowDoubleClick = (event: Event) => {
        setSelectedEventId(event.id);
    };

    const handleBodyUpdate = async (eventId: number, body: string) => {
        await monitorApi.updateEventBody(eventId, body, email || undefined);
        // Fetch the updated event and update it in place
        const response = await monitorApi.getEvent(eventId);
        setEvents((prevEvents) => prevEvents.map((event) => (event.id === eventId ? response.event : event)));
    };

    const displayedEvents = events.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <>
            {/* Filter Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        color="primary"
                        size="small"
                        value={filter}
                        exclusive
                        onChange={handleFilterChange}
                    >
                        <ToggleButton value="all">{isMobile ? 'All' : 'All Events'}</ToggleButton>
                        <ToggleButton value="incomplete">Incomplete</ToggleButton>
                        <ToggleButton value="warning">Warning</ToggleButton>
                        <ToggleButton value="disabled">Disabled</ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                        color="primary"
                        size="small"
                        value={destination}
                        exclusive
                        onChange={handleDestinationChange}
                    >
                        <ToggleButton value="all" sx={{ width: 40 }}>
                            All
                        </ToggleButton>
                        <ToggleButton value="mfi" sx={{ width: 40 }}>
                            MFI
                        </ToggleButton>
                        <ToggleButton value="stripe">Stripe</ToggleButton>
                        <ToggleButton value="qb" sx={{ width: 40 }}>
                            QB
                        </ToggleButton>
                        <ToggleButton value="drip">Drip</ToggleButton>
                        <ToggleButton value="icontact">iContact</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={search}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        sx={{ width: { xs: '100%', sm: 200 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleSearchClear} edge="end">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : `${events.length} events loaded`}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 700 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ py: 1 }}>ID</TableCell>
                                <TableCell sx={{ py: 1 }}>Destination</TableCell>
                                <TableCell sx={{ py: 1 }}>Status</TableCell>
                                <TableCell sx={{ py: 1 }}>Retries</TableCell>
                                <TableCell sx={{ py: 1 }}>Description</TableCell>
                                <TableCell sx={{ py: 1 }}>Origin</TableCell>
                                <TableCell sx={{ py: 1 }}>Outcome</TableCell>
                                <TableCell sx={{ py: 1 }}>Created</TableCell>
                                <TableCell sx={{ py: 1 }}>Completed</TableCell>
                                <TableCell sx={{ py: 1 }}>Duration</TableCell>
                                <TableCell sx={{ py: 1 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedEvents.map((event) => (
                                <TableRow
                                    key={event.id}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                    onDoubleClick={() => handleRowDoubleClick(event)}
                                >
                                    <TableCell>{event.id}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                        {event.functionName.replace('handle', '')}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={event.status}
                                            color={getStatusColor(event.status) as any}
                                            size="small"
                                            sx={getStatusSx(event.status)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {event.retryCount > 0 ? (
                                            <Chip
                                                label={event.retryCount}
                                                color={getStatusColor(event.status) as any}
                                                size="small"
                                                sx={getStatusSx(event.status)}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {event.description}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 85 }}>{event.originatorName}</TableCell>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {event.outcome || '-'}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 120 }}>{formatDate(event.createdTime)}</TableCell>
                                    <TableCell sx={{ maxWidth: 120 }}>{formatDate(event.completedTime)}</TableCell>
                                    <TableCell>{event.executionMs ?? '-'} ms</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(event)}
                                                    color="primary"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            {canRetry && (
                                                <Tooltip title="Resend">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRetryEvent(event.id)}
                                                        color="warning"
                                                        disabled={retryingEventId === event.id}
                                                    >
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading events...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && displayedEvents.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography color="text.secondary">No events found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={events.length + (hasMore ? 1 : 0)} // Add 1 if there are more to indicate more data
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePageSizeChange}
                />
            </Paper>

            <EventDetailsDialog
                eventId={selectedEventId}
                open={!!selectedEventId}
                onClose={handleCloseDetails}
                onRetry={handleRetryEvent}
                onMarkDead={handleMarkDeadEvent}
                onMarkHandled={handleMarkHandledEvent}
                onMarkWarning={handleMarkWarningEvent}
                onBodyUpdate={handleBodyUpdate}
                retryingEventId={retryingEventId}
                markingDeadEventId={markingDeadEventId}
                markingHandledEventId={markingHandledEventId}
                markingWarningEventId={markingWarningEventId}
            />
        </>
    );
}
